// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SparseOrganization } from '@/account/api/types';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import {
  CREATE_CUSTOMER_OPTION_ID,
  CompanyAutocomplete,
} from '@/components/company-autocomplete';
import { getCustomers } from '@/customer/api/get-customers';
import { Customer } from '@/customer/api/types';
import { SparseCustomer } from '@/sparse/api/types';
import { FilterOptionsState, createFilterOptions } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { reject } from 'lodash-es';
import { ForwardedRef, forwardRef, useState } from 'react';

/**
 * Base filter function for company autocomplete.
 * Created using MUI's createFilterOptions for consistent filtering behavior.
 */
const filter = createFilterOptions<SparseCustomer>();

/**
 * Returns a list of company options by combining the organization and
 * customers. If either the customers or organization is undefined, an empty
 * array is returned.
 *
 * @param customers - A list of customers.
 * @param organization - The current user's organization.
 */
function getOptions(
  customers: Customer[],
  organization: SparseOrganization | undefined,
  showManufacturer: boolean
): SparseCustomer[] {
  if (organization === undefined) {
    return [];
  }

  const options = customers.sort((customer, other) => {
    if (customer.isArchived === other.isArchived) {
      return customer.name.localeCompare(other.name);
    }

    return customer.isArchived ? 1 : -1;
  });

  if (!showManufacturer) {
    return [...options];
  }

  return [
    {
      ...organization,
      isCustomer: false,
      isSupplier: false,
      isArchived: false,
      isManufacturer: true,
    },
    ...options,
  ];
}

interface Props {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * The currently selected company.
   */
  value: SparseCustomer | null;

  /**
   * A callback fired when the selected company changes.
   */
  onChange: (value: SparseCustomer | null) => void;

  /**
   * Callback fired when user chooses to create a new customer.
   * @param name - The name entered for the new customer
   */
  onCreate?: (name: string) => void;

  /**
   * If true, the manufacturer (current organization) is included in the
   * options list and clearing the selection resets to the manufacturer.
   * If false, only customers and suppliers are shown.
   * @default true
   */
  showManufacturer?: boolean;

  /**
   * If true, the clear button is hidden and the selection can't be cleared.
   */
  disableClearable?: boolean;
}

/**
 * Renders a company autocomplete component with options from the remote API.
 * This function also forwards a ref to the autocomplete component.
 */
function CompanySearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { user } = useCurrentUser();
  const currentOrganization = user.organization!;

  const [showArchived, setShowArchived] = useState(false);

  const customersQuery = useQuery({
    queryKey: ['customer'],
    queryFn: () => getCustomers({ isArchived: 'all' }),
  });

  const customers =
    customersQuery.data?.customers.filter((customer) => {
      if (customer.isArchived) {
        return showArchived;
      }

      return true;
    }) ?? [];

  const showManufacturer = props.showManufacturer ?? true;
  const companies = getOptions(
    customers,
    currentOrganization,
    showManufacturer
  );
  const options =
    props.value && !companies.some((company) => company.id === props.value?.id)
      ? [props.value, ...companies]
      : companies;

  /**
   * Custom filter function for the autocomplete options.
   * Handles both filtering existing companies and adding the create option.
   *
   * @param options - List of all company options
   * @param state - Current filter state including input value
   * @returns Filtered list of options, potentially including create option
   */
  const filterOptions = (
    options: SparseCustomer[],
    state: FilterOptionsState<SparseCustomer>
  ) => {
    // Apply base text filtering
    const filtered = filter(options, state);

    // Show only active companies when no search input
    if (state.inputValue === '') {
      return reject(filtered, 'isArchived');
    }

    // If onCreate is not provided, just return filtered results
    if (!props.onCreate) {
      return filtered;
    }

    // Check for exact name match (case-insensitive) among customers only
    const isMatch = filtered.some(({ name, isCustomer }) => {
      return (
        isCustomer && name.toLowerCase() === state.inputValue.toLowerCase()
      );
    });

    // Return filtered list if exact match found
    if (isMatch) {
      return filtered;
    }

    // Add create option if no exact match and input is not empty
    return [
      ...filtered,
      {
        id: CREATE_CUSTOMER_OPTION_ID,
        key: CREATE_CUSTOMER_OPTION_ID,
        name: state.inputValue,
        isArchived: false,
        isCustomer: true,
        isSupplier: false,
        isManufacturer: false,
      },
    ];
  };

  const handleChange = (_: unknown, value: SparseCustomer | null) => {
    if (value === null) {
      if (!showManufacturer) {
        return props.onChange(null);
      }

      return props.onChange({
        id: currentOrganization.id,
        key: currentOrganization.key,
        name: currentOrganization.name,
        isCustomer: false,
        isSupplier: false,
        isArchived: false,
        isManufacturer: true,
      });
    }
    // Handle selecting create option
    if (value && value.id === CREATE_CUSTOMER_OPTION_ID && props.onCreate) {
      return props.onCreate(value.name);
    }

    // Handle selecting existing company or clearing selection
    props.onChange(value);
  };

  const handleChangeInput = (_: unknown, value: string) => {
    if (value === '') {
      return setShowArchived(false);
    }

    setShowArchived(true);
  };

  return (
    <CompanyAutocomplete
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      options={options}
      loading={customersQuery.isLoading}
      onChange={handleChange}
      helperText={props.helperText}
      disableClearable={props.disableClearable}
      onInputChange={handleChangeInput}
      filterOptions={filterOptions}
      loadingText="Loading..."
    />
  );
}

/**
 * A controlled component for selecting a company from a list of options from
 * the remote API.
 */
export const CompanySearch = forwardRef(CompanySearchRenderer);
